import React, { useState, useEffect } from 'react';
import {
    Grid, Box, Typography, Button, TextField, Card, CardContent, Divider
} from '@mui/material';
import Controls from '../../components/controls/Controls';
import { fetchData } from '../../services/ApiServices';
import { urlTariffs } from '../../config';
import { logActionType } from '../../services/MyCollections';
import { logActivity } from '../../services/ApiServices';

export default function AccountantTariffs({ token, userName, onNotify, onSuccess, darkTheme }) {
    const [mode, setMode] = useState('edit'); // 'edit' | 'add'
    const [formData, setFormData] = useState({});
    const [tariffs, setTariffs] = useState([]);
    const [errors, setErrors] = useState({});
    const [selectedTariff, setSelectedTariff] = useState(null);
    const regex = /^\d+$/;

    const notify = (msg, severity = 'success') => {
        if (onNotify) {
            onNotify(msg, severity);
        } else if (onSuccess) {
            onSuccess(msg);
        }
    };

    const mapToSelect = (item) => ({
        key: item.id,
        value: item.description,
    });

    const loadTariffs = () => {
        fetchData(urlTariffs + '/allForSelect', mapToSelect, setTariffs, console.error, token);
    };

    useEffect(() => {
        loadTariffs();
    }, []);

    const handleSwitchMode = (newMode) => {
        setMode(newMode);
        setSelectedTariff(null);
        setFormData({});
        setErrors({});
        if (newMode === 'edit') {
            loadTariffs();
        }
    };

    const handleTariffChange = (event) => {
        const selectedValue = event.target.value;
        setSelectedTariff(selectedValue);
        setErrors({});
        if (selectedValue) {
            fetchLocal(urlTariffs + "/" + selectedValue);
        } else {
            setFormData({});
        }
    };

    const fetchLocal = (url) => {
        fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(resp => resp.json())
            .then(data => setFormData(data))
            .catch(error => console.error(error));
    };

    const localOnChange = (e) => {
        const { name, value } = e.target;
        if (name !== "description" && value !== "" && !regex.test(value)) {
            return;
        }
        validateField(name, value);
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const validateField = (name, value) => {
        setErrors(prev => {
            const next = { ...prev };
            if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
                next[name] = 'Pole nie może być puste.';
            } else {
                delete next[name];
            }
            return next;
        });
    };

    const validateFormOnSubmit = () => {
        let formIsValid = true;
        let validationErrors = {};

        const requiredFields = [
            'description', 'baseFeeSpZoo', 'baseFeeJDG', 'documentSpZoo', 'documentJDG',
            'personelServiceManagement', 'personelServiceWork', 'personelServiceEmployeeContract',
            'additionalDeclaration', 'correctionOfDeclaration', 'finReportSmallClient',
            'finReportMediumClient', 'finReportLargeClient', 'balanceSheetOnRequest',
            'estonianCit', 'premiumPersonnel', 'additionalConsultation', 'otherServices',
            'sfToKrsAssistance'
        ];

        requiredFields.forEach(field => {
            const val = formData?.[field];
            if (val === null || val === undefined || (typeof val === 'string' && val.trim() === '')) {
                validationErrors[field] = 'Pole jest wymagane';
                formIsValid = false;
            }
        });

        setErrors(validationErrors);
        return formIsValid;
    };

    const handleSubmit = () => {
        if (!validateFormOnSubmit()) {
            notify('Uzupełnij wszystkie wymagane pola formularza.', 'warning');
            return;
        }

        const isAdd = mode === 'add';
        const url = isAdd ? urlTariffs + `/add` : urlTariffs + `/edit`;

        fetch(url, {
            method: "POST",
            body: JSON.stringify(formData),
            headers: {
                'content-type': "application/json",
                'Authorization': `Bearer ${token}`
            }
        })
            .then(resp => {
                if (resp.status === 200) {
                    const action = isAdd
                        ? logActionType.ADD_NEW_ACCOUNTANT_TARIFF
                        : logActionType.EDIT_ACCOUNTANT_TARIFF;
                    logActivity(action + formData.description, userName, token);
                    notify(isAdd ? 'Cennik księgowych został pomyślnie dodany!' : 'Cennik księgowych został pomyślnie zaktualizowany!', 'success');
                    if (isAdd) {
                        setFormData({});
                        handleSwitchMode('edit');
                    }
                    return resp.json();
                } else if (resp.status === 400) {
                    notify('Cennik o podanej nazwie już istnieje w bazie.', 'warning');
                } else {
                    throw new Error(`HTTP error! Status: ${resp.status}`);
                }
            })
            .catch(error => {
                console.error('Błąd zapisu stawek:', error);
                notify('Wystąpił błąd podczas zapisywania stawek.', 'error');
            });
    };

    const renderTariffForm = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Grupa 1: Stawki podstawowe */}
            <Card
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                    backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.3)' : '#F8FAFC'
                }}
            >
                <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: darkTheme ? '#93C5FD' : '#1D4ED8', mb: 1.5 }}>
                        <i className="fas fa-calculator" style={{ marginRight: 8 }}></i>
                        Stawki podstawowe i rozliczenie dokumentów
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                name="description"
                                value={formData.description ?? ''}
                                placeholder="Wprowadź nazwę cennika"
                                label="Nazwa cennika stawek"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.description}
                                helperText={errors.description}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                name="baseFeeSpZoo"
                                value={formData.baseFeeSpZoo ?? ''}
                                placeholder="0"
                                label="Opłata bazowa Sp. z o.o. (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.baseFeeSpZoo}
                                helperText={errors.baseFeeSpZoo}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                name="baseFeeJDG"
                                value={formData.baseFeeJDG ?? ''}
                                placeholder="0"
                                label="Opłata bazowa JDG (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.baseFeeJDG}
                                helperText={errors.baseFeeJDG}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                name="documentSpZoo"
                                value={formData.documentSpZoo ?? ''}
                                placeholder="0"
                                label="Dokument Sp. z o.o. (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.documentSpZoo}
                                helperText={errors.documentSpZoo}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                name="documentJDG"
                                value={formData.documentJDG ?? ''}
                                placeholder="0"
                                label="Dokument JDG (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.documentJDG}
                                helperText={errors.documentJDG}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Grupa 2: Obsługa kadrowa */}
            <Card
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                    backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.3)' : '#F8FAFC'
                }}
            >
                <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: darkTheme ? '#86EFAC' : '#047857', mb: 1.5 }}>
                        <i className="fas fa-users-cog" style={{ marginRight: 8 }}></i>
                        Obsługa kadrowo-płacowa
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                name="personelServiceManagement"
                                value={formData.personelServiceManagement ?? ''}
                                placeholder="0"
                                label="Zarząd z powołania (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.personelServiceManagement}
                                helperText={errors.personelServiceManagement}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                name="personelServiceWork"
                                value={formData.personelServiceWork ?? ''}
                                placeholder="0"
                                label="Umowa o pracę (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.personelServiceWork}
                                helperText={errors.personelServiceWork}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                name="personelServiceEmployeeContract"
                                value={formData.personelServiceEmployeeContract ?? ''}
                                placeholder="0"
                                label="Umowa zlecenie / dzieło (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.personelServiceEmployeeContract}
                                helperText={errors.personelServiceEmployeeContract}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                name="premiumPersonnel"
                                value={formData.premiumPersonnel ?? ''}
                                placeholder="0"
                                label="Kadry premium (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.premiumPersonnel}
                                helperText={errors.premiumPersonnel}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Grupa 3: Deklaracje, CIT i doradztwo */}
            <Card
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                    backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.3)' : '#F8FAFC'
                }}
            >
                <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: darkTheme ? '#FDE047' : '#B45309', mb: 1.5 }}>
                        <i className="fas fa-file-signature" style={{ marginRight: 8 }}></i>
                        Deklaracje, CIT oraz doradztwo
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                name="additionalDeclaration"
                                value={formData.additionalDeclaration ?? ''}
                                placeholder="0"
                                label="Dodatkowa deklaracja (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.additionalDeclaration}
                                helperText={errors.additionalDeclaration}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                name="correctionOfDeclaration"
                                value={formData.correctionOfDeclaration ?? ''}
                                placeholder="0"
                                label="Korekta deklaracji (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.correctionOfDeclaration}
                                helperText={errors.correctionOfDeclaration}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                name="balanceSheetOnRequest"
                                value={formData.balanceSheetOnRequest ?? ''}
                                placeholder="0"
                                label="Bilans na żądanie (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.balanceSheetOnRequest}
                                helperText={errors.balanceSheetOnRequest}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                name="estonianCit"
                                value={formData.estonianCit ?? ''}
                                placeholder="0"
                                label="CIT estoński (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.estonianCit}
                                helperText={errors.estonianCit}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                name="additionalConsultation"
                                value={formData.additionalConsultation ?? ''}
                                placeholder="0"
                                label="Dodatkowa konsultacja (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.additionalConsultation}
                                helperText={errors.additionalConsultation}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                name="otherServices"
                                value={formData.otherServices ?? ''}
                                placeholder="0"
                                label="Inne usługi (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.otherServices}
                                helperText={errors.otherServices}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Grupa 4: Sprawozdawczość finansowa i KRS */}
            <Card
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                    backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.3)' : '#F8FAFC'
                }}
            >
                <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: darkTheme ? '#C084FC' : '#7E22CE', mb: 1.5 }}>
                        <i className="fas fa-balance-scale" style={{ marginRight: 8 }}></i>
                        Sprawozdania finansowe i obsługa KRS
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                name="finReportSmallClient"
                                value={formData.finReportSmallClient ?? ''}
                                placeholder="0"
                                label="SF mały klient (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.finReportSmallClient}
                                helperText={errors.finReportSmallClient}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                name="finReportMediumClient"
                                value={formData.finReportMediumClient ?? ''}
                                placeholder="0"
                                label="SF średni klient (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.finReportMediumClient}
                                helperText={errors.finReportMediumClient}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                name="finReportLargeClient"
                                value={formData.finReportLargeClient ?? ''}
                                placeholder="0"
                                label="SF duży klient (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.finReportLargeClient}
                                helperText={errors.finReportLargeClient}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                name="sfToKrsAssistance"
                                value={formData.sfToKrsAssistance ?? ''}
                                placeholder="0"
                                label="Pomoc SF do KRS (zł)"
                                variant="outlined"
                                size="small"
                                onChange={localOnChange}
                                fullWidth
                                error={!!errors.sfToKrsAssistance}
                                helperText={errors.sfToKrsAssistance}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Controls.PremiumButton
                    variant="green"
                    onClick={handleSubmit}
                    label={mode === 'add' ? 'Dodaj nowy cennik księgowych' : 'Zapisz zmiany stawek'}
                    icon="fas fa-save"
                />
            </Box>
        </Box>
    );

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: darkTheme ? '#F8FAFC' : '#1E293B', mb: 0.5 }}>
                    Stawki Księgowych
                </Typography>
                <Typography variant="body2" sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                    Definiuj modele wynagrodzeń księgowych za obsługę firm, dokumenty ponad limit, kadry oraz sprawozdawczość.
                </Typography>
            </Box>

            {/* Segmented Sub-Navigation */}
            <Box
                sx={{
                    display: 'inline-flex',
                    p: '4px',
                    borderRadius: '10px',
                    backgroundColor: darkTheme ? 'rgba(255, 255, 255, 0.05)' : '#F1F5F9',
                    mb: 3,
                    border: '1px solid',
                    borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                    gap: 0.5
                }}
            >
                <Button
                    onClick={() => handleSwitchMode('edit')}
                    sx={{
                        px: 2.5,
                        py: 0.8,
                        borderRadius: '7px',
                        textTransform: 'none',
                        fontSize: '13.5px',
                        fontWeight: 600,
                        color: mode === 'edit'
                            ? '#FFFFFF'
                            : (darkTheme ? '#94A3B8' : '#64748B'),
                        backgroundColor: mode === 'edit'
                            ? (darkTheme ? '#2563EB' : '#1D4ED8')
                            : 'transparent',
                        boxShadow: mode === 'edit' ? '0 2px 6px rgba(37, 99, 235, 0.3)' : 'none',
                        '&:hover': {
                            backgroundColor: mode === 'edit'
                                ? (darkTheme ? '#1D4ED8' : '#1E40AF')
                                : (darkTheme ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)')
                        }
                    }}
                >
                    <i className="fas fa-edit" style={{ marginRight: 8 }}></i>
                    Edytuj istniejące stawki
                </Button>
                <Button
                    onClick={() => handleSwitchMode('add')}
                    sx={{
                        px: 2.5,
                        py: 0.8,
                        borderRadius: '7px',
                        textTransform: 'none',
                        fontSize: '13.5px',
                        fontWeight: 600,
                        color: mode === 'add'
                            ? '#FFFFFF'
                            : (darkTheme ? '#94A3B8' : '#64748B'),
                        backgroundColor: mode === 'add'
                            ? (darkTheme ? '#10B981' : '#059669')
                            : 'transparent',
                        boxShadow: mode === 'add' ? '0 2px 6px rgba(16, 185, 129, 0.3)' : 'none',
                        '&:hover': {
                            backgroundColor: mode === 'add'
                                ? (darkTheme ? '#059669' : '#047857')
                                : (darkTheme ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)')
                        }
                    }}
                >
                    <i className="fas fa-plus" style={{ marginRight: 8 }}></i>
                    Dodaj nowy cennik stawek
                </Button>
            </Box>

            {/* WIDOK: Edycja stawek */}
            {mode === 'edit' && (
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2.5,
                        borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                        backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.2)' : '#FFFFFF',
                        mb: 3
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ maxWidth: 480, mb: 3 }}>
                            <Controls.Select
                                name="tariffId"
                                label="Wybierz cennik księgowych"
                                value={selectedTariff}
                                onChange={handleTariffChange}
                                options={tariffs}
                                error={errors.tariffId}
                            />
                        </Box>

                        {selectedTariff && formData.description !== undefined && (
                            <>
                                <Divider sx={{ my: 3 }} />
                                {renderTariffForm()}
                            </>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* WIDOK: Dodawanie nowych stawek */}
            {mode === 'add' && (
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2.5,
                        borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                        backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.2)' : '#FFFFFF',
                        mb: 3
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: darkTheme ? '#F1F5F9' : '#0F172A', mb: 2.5 }}>
                            Nowy cennik wynagrodzeń księgowych
                        </Typography>
                        {renderTariffForm()}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}
